import { useMemo, useRef, useCallback } from 'react';
import { setRef } from '../utils/ref.js';

/**
 * Merge a set of refs into a single ref callback. The returned ref callback will be stable as
 * long as the input refs are stable. This function is intended to be called with a known set of
 * refs, rather than an array that may change over time.
 *
 * If all the provided refs are nullish, `undefined` will be returned. The types are set up so that
 * if _any_ of the provided refs are non-nullable, the return type will not include `| undefined`.
 */
function useMergeRefs(...refs) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return useMemo(() => {
        if (refs.every((ref) => ref == null))
            return undefined;
        return (value) => {
            for (const ref of refs) {
                setRef(ref, value);
            }
        };
        // refs is already an array so we just want to pass it through
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, refs);
}
function useMergedRefObject(...refs) {
    const targetRef = useRef(null);
    const mergedCallback = useCallback((value) => {
        targetRef.current = value;
        for (const ref of refs) {
            setRef(ref, value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, refs);
    return [targetRef, mergedCallback];
}

export { useMergeRefs, useMergedRefObject };
//# sourceMappingURL=use-merge-refs.js.map
